import { useTranslation } from 'react-i18next';

import updateInventory from '@/shared/api/inventory/updateInventory';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import DialogName from '@/shared/types/dialogName.enum';
import { InventoryGetModel } from '@/shared/types/inventory.type';

type OnUpdateDialogState = ReturnType<
  typeof useModalDialog<unknown, DialogName>
>['onUpdateDialogState'];

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type OnUpdateProgressDialog = ReturnType<
  typeof useProgressDialog
>['onUpdateProgressDialog'];

type Args = {
  onUpdateDialogState: OnUpdateDialogState;
  onUpdateNotificationBar: OnUpdateNotificationBar;
  onUpdateProgressDialog: OnUpdateProgressDialog;
};

const useInventoryOperations = ({
  onUpdateDialogState,
  onUpdateNotificationBar,
  onUpdateProgressDialog,
}: Args) => {
  const redirectLogin = useRedirectLogin();
  const { t } = useTranslation(['inventory', 'common']);

  const onUpdateInventoryItems = async (
    metadataDetails: Record<string, unknown>,
    items: InventoryGetModel[],
  ) => {
    onUpdateProgressDialog({
      title: t('inventory:updateInventoryInProgress'),
      open: true,
      progress: 0,
    });

    // Todo: https://waveinc.atlassian.net/browse/BRABF-92
    // after this issue will be fixed, updateInventory will require refactor
    const formatRequestBody = items.map((item) => ({
      ...item,
      ...metadataDetails,
    })) as InventoryGetModel[];

    const updateInventoryResponse = await updateInventory({
      metadataDetails: formatRequestBody,
      onUnauthorized: redirectLogin,
      onProgress: (progress) => onUpdateProgressDialog({ progress }),
    });

    if (updateInventoryResponse.every((item) => item.success)) {
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.UpdateInventoryItems, { open: false });
      onUpdateNotificationBar({
        message: t('inventory:updateInventorySuccess'),
        severity: 'success',
        open: true,
      });
    } else {
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.UpdateInventoryItems, {
        open: true,
        errors: updateInventoryResponse
          .filter((res) => !res.success)
          .map((res) => res.msg),
      });
    }
  };

  return {
    onUpdateInventoryItems,
  };
};

export default useInventoryOperations;
